import React from 'react';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Text } from '@lifeomic/chroma-react/components/Text';
import clsx from 'clsx';
import SurveyItem from '../../util/SurveyItem';
import { LaunchCourseButton } from '../../../LifeologyCourse';
import SurveyItemContainer from '../SurveyItemContainer';
import SurveyField from '../SurveyField';
import { FieldProps } from 'formik';
import withValidation, {
  WithValidationInjectedProps,
} from '../hoc/withValidation';
import withCheckForEnablement, {
  WithCheckForEnablementInjectedProps,
} from '../hoc/withCheckForEnablement';
import { withIsReadOnly, WithIsReadOnlyInjectedProps } from '../Contexts';
import getSurveyItemTestId from '../../util/getSurveyItemTestId';
import { getTestProps } from '../../../../util/testProps';
import { CourseState } from '../../../LifeologyCourse/LifeologyCourse';

export const testIds = {
  courseLaunchButton: (linkId: string) => getSurveyItemTestId(linkId),
};

export const useStyles = makeStyles((theme: Theme) => ({
  labelText: {
    color: theme.palette.text.secondary,
  },
  modalContent: {
    padding: 0,
    overflow: 'hidden',
  },
  errorText: {
    marginTop: theme.spacing(1),
    color: theme.palette.error.main,
  },
  errorLabel: {
    color: theme.palette.error.main,
  },
  launchCourseButtonWrapper: {
    marginTop: theme.spacing(1),
  },
}));

export interface FieldComponentOwnProps {
  isReadOnly: boolean;
  classes: { [key: string]: string };
  surveyItem: SurveyItem;
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
}

export type FieldComponentProps = FieldComponentOwnProps &
  FieldProps<any> &
  WithValidationInjectedProps;

const FieldComponent = (props: FieldComponentProps) => {
  const classes = useStyles(props);

  const onCourseStateUpdate = (stateUpdate: CourseState) => {
    if (stateUpdate === CourseState.LastPageLoaded) {
      props.form.setFieldValue(props.field.name, 1.0);
    }
  };

  const {
    error,
    field: { ...inputProps },
    shouldShowError,
    surveyItem,
  } = props;

  return (
    <React.Fragment>
      <div className={classes.launchCourseButtonWrapper}>
        <LaunchCourseButton
          {...getTestProps(testIds.courseLaunchButton(inputProps.name))}
          onCourseStateUpdate={onCourseStateUpdate}
          hideMobileSurveyCloseButton
          url={surveyItem.lifeologyCourseUrl}
        />
      </div>
      {shouldShowError && (
        <Text size="caption" className={classes.errorText}>
          {error}
        </Text>
      )}
    </React.Fragment>
  );
};

export interface LifeologyCourseOwnProps {
  surveyItem: SurveyItem;
}

export type LifeologyCourseProps = LifeologyCourseOwnProps &
  WithCheckForEnablementInjectedProps &
  WithIsReadOnlyInjectedProps &
  WithValidationInjectedProps;

const LifeologyCourseSurveyItem = (props: LifeologyCourseProps) => {
  const { error, isReadOnly, surveyItem, shouldShowError, validate } = props;
  const classes = useStyles({});

  const getQuestionText = () => {
    const { prefix, text, showsRequiredAsterisk, label } = surveyItem;

    let headerText = '';
    if (prefix) {
      headerText = `${showsRequiredAsterisk ? '* ' : ''}(${prefix}) ${text}`;
    } else {
      headerText = label || '';
    }

    if (!headerText) return null;

    return (
      <Text
        size="body"
        className={clsx(
          classes.labelText,
          shouldShowError && classes.errorLabel,
        )}
      >
        {headerText}
      </Text>
    );
  };

  return (
    <SurveyItemContainer surveyItem={surveyItem}>
      <div>
        {getQuestionText()}
        <SurveyField
          responseAnswerType={surveyItem.responseValueType}
          name={surveyItem.link}
          surveyItem={surveyItem}
          component={FieldComponent}
          classes={classes}
          isReadOnly={isReadOnly}
          validate={validate}
          error={error}
          shouldShowError={shouldShowError}
        />
      </div>
    </SurveyItemContainer>
  );
};

export default withCheckForEnablement(
  withIsReadOnly(withValidation(LifeologyCourseSurveyItem)),
);
